// RecipePageEditor — in-context edit mode for a Recipe Book page.
//
// RecipePage early-returns into this component when the user taps
// EDIT: the page itself becomes editable (same cream paper, same
// brown ink) instead of bouncing to a form. Scope (v1):
//   • title, serves (value edit, no auto-rescale)
//   • ingredients: amount + item text, remove, add (with automatic
//     canonical binding via the longest-match resolver)
//   • steps: title + instruction text (no add/remove/reorder yet)
//
// Storage discipline: the draft clones bookRow.recipe — the BASE
// recipe, never RecipePage's scaleRecipe() output — and amounts are
// edited VERBATIM (the user's text is what gets stored; preferred-
// unit conversion stays a display-layer transform on the read view).
// ingredient.qty re-syncs from the amount text on blur via
// parseAmountString so pantry deduction stays accurate, and drops to
// null when unparseable (a stale qty is worse than none).
//
// Saving routes through the shared pipeline in src/lib/recipeEdits.js:
// finalizeRecipeEdits (uses[] prune + timer re-inference + technique
// re-validation) → decideSaveMode:
//   own row    → onSaveInPlace(id, recipe)   — slug stays stable
//   not owned  → onForkSave(payload, "custom") — forkedFrom stamped,
//                inline name input defaults "My {title}"

import { useMemo, useState } from "react";
import { FONT } from "../lib/tokens";
import { findIngredient, inferCanonicalFromNameLongestMatch } from "../data/ingredients";
import { parseAmountString } from "../lib/nutrition";
import { finalizeRecipeEdits, decideSaveMode, buildForkPayload } from "../lib/recipeEdits";

// Paper-ink palette lifted from RecipePage so edit mode reads as the
// same physical page, just annotated.
const INK = {
  body: "#3A2A12",
  warm: "#7C5E2C",
  deep: "#5C3F18",
  faint: "#9C7E40",
  line: "rgba(124,94,44,0.30)",
  fill: "rgba(124,94,44,0.08)",
  rose: "#A33B2A",
};

const kickerStyle = {
  fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
  color: INK.faint, letterSpacing: "0.16em", textTransform: "uppercase",
};

const fieldStyle = {
  fontFamily: FONT.forma, fontSize: 13, color: INK.body,
  background: "rgba(255,255,255,0.55)",
  border: `1px solid ${INK.line}`,
  borderRadius: 8, padding: "8px 10px",
  outline: "none", boxSizing: "border-box",
};

function deepClone(v) {
  if (typeof structuredClone === "function") return structuredClone(v);
  return JSON.parse(JSON.stringify(v));
}

export default function RecipePageEditor({ bookRow, userId, onSaveInPlace, onForkSave, onExit }) {
  const original = bookRow?.recipe || {};
  const [draft, setDraft] = useState(() => deepClone(original));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const saveMode = useMemo(
    () => decideSaveMode({ bookRow, viewerId: userId }),
    [bookRow, userId],
  );
  const [forkName, setForkName] = useState(() => `My ${original.title || "Recipe"}`);

  const patch = (fn) => {
    setDraft((d) => {
      const next = deepClone(d);
      fn(next);
      return next;
    });
    setDirty(true);
  };

  // Re-sync structured qty from the verbatim amount text. Runs on
  // blur (not per keystroke) so half-typed amounts don't thrash.
  const syncQty = (idx) => {
    patch((d) => {
      const ing = d.ingredients?.[idx];
      if (!ing) return;
      const canonical = ing.ingredientId ? findIngredient(ing.ingredientId) : null;
      const parsed = canonical ? parseAmountString(ing.amount, canonical) : null;
      if (parsed) ing.qty = parsed;
      else delete ing.qty;
    });
  };

  const [newIngName, setNewIngName] = useState("");
  const [newIngAmount, setNewIngAmount] = useState("");
  const addIngredient = () => {
    const name = newIngName.trim();
    if (!name) return;
    const amount = newIngAmount.trim();
    const ingredientId = inferCanonicalFromNameLongestMatch(name) || null;
    const canonical = ingredientId ? findIngredient(ingredientId) : null;
    const qty = canonical && amount ? parseAmountString(amount, canonical) : null;
    patch((d) => {
      if (!Array.isArray(d.ingredients)) d.ingredients = [];
      d.ingredients.push({
        amount: amount || null,
        item: name,
        ingredientId,
        ...(qty ? { qty } : {}),
      });
    });
    setNewIngName("");
    setNewIngAmount("");
  };

  const handleDone = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      const { recipe: finalRecipe, diff } = finalizeRecipeEdits(draft, original);
      if (saveMode === "inPlace") {
        await onSaveInPlace?.(bookRow.id, finalRecipe);
      } else {
        const payload = buildForkPayload(finalRecipe, bookRow, { title: forkName, diff });
        await onForkSave?.(payload, "custom");
      }
      onExit?.({ saved: true, mode: saveMode });
    } catch (e) {
      setError(e?.message || "Couldn't save — try again?");
      setSaving(false);
    }
  };

  const ingredients = Array.isArray(draft.ingredients) ? draft.ingredients : [];
  const steps = Array.isArray(draft.steps) ? draft.steps : [];

  return (
    // Keyboard handling is fully automatic: useKeyboardOffset's
    // universal padder finds this scrollable container when a field
    // inside it focuses and grows its bottom padding by the live
    // keyboard height, then the global focusin handler centers the
    // field. No per-surface keyboard code — that's the one-strategy
    // rule.
    <div style={{
      width: "100%", height: "100%",
      display: "flex", flexDirection: "column",
      padding: "26px 28px 18px",
      position: "relative",
      overflowY: "auto", overflowX: "hidden",
      WebkitOverflowScrolling: "touch",
    }}>
      <div style={{ ...kickerStyle, color: INK.deep }}>
        EDITING {saveMode === "fork" ? "· SAVES AS YOUR VERSION" : "· UPDATES THIS RECIPE"}
      </div>

      {/* Title */}
      <input
        value={draft.title || ""}
        onChange={(e) => patch((d) => { d.title = e.target.value; })}
        placeholder="Recipe title"
        style={{
          ...fieldStyle,
          marginTop: 10,
          fontFamily: FONT.serif, fontStyle: "italic",
          fontSize: 22, color: INK.body,
        }}
      />

      {/* Serves — value edit, deliberately NOT a rescale (the read
          view's servings stepper handles scaled display; this fixes
          a wrong serves count). */}
      <div style={{ marginTop: 12, display: "inline-flex", alignItems: "center", gap: 8 }}>
        <span style={kickerStyle}>SERVES</span>
        <button
          type="button"
          className="mcm-focusable"
          onClick={() => patch((d) => { d.serves = Math.max(1, (Number(d.serves) || 1) - 1); })}
          style={{
            width: 28, height: 28, borderRadius: 14, cursor: "pointer",
            background: INK.fill, border: `1px solid ${INK.line}`,
            fontFamily: FONT.forma, fontSize: 14, color: INK.deep,
            touchAction: "manipulation",
          }}
        >−</button>
        <span style={{ fontFamily: FONT.forma, fontSize: 14, fontWeight: 600, color: INK.body, minWidth: 18, textAlign: "center" }}>
          {Number(draft.serves) || "—"}
        </span>
        <button
          type="button"
          className="mcm-focusable"
          onClick={() => patch((d) => { d.serves = Math.min(24, (Number(d.serves) || 1) + 1); })}
          style={{
            width: 28, height: 28, borderRadius: 14, cursor: "pointer",
            background: INK.fill, border: `1px solid ${INK.line}`,
            fontFamily: FONT.forma, fontSize: 14, color: INK.deep,
            touchAction: "manipulation",
          }}
        >+</button>
      </div>

      {/* Ingredients */}
      <div style={{ ...kickerStyle, marginTop: 18, marginBottom: 8 }}>
        INGREDIENTS · {ingredients.length}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {ingredients.map((ing, i) => (
          <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              value={ing.amount || ""}
              onChange={(e) => patch((d) => { d.ingredients[i].amount = e.target.value; })}
              onBlur={() => syncQty(i)}
              placeholder="amount"
              style={{ ...fieldStyle, width: 86, fontStyle: "italic", color: INK.warm, flexShrink: 0 }}
            />
            <input
              value={ing.item || ing.name || ""}
              onChange={(e) => patch((d) => { d.ingredients[i].item = e.target.value; })}
              placeholder="ingredient"
              style={{ ...fieldStyle, flex: 1, minWidth: 0 }}
            />
            <button
              type="button"
              className="mcm-focusable"
              aria-label={`Remove ${ing.item || "ingredient"}`}
              onClick={() => patch((d) => { d.ingredients.splice(i, 1); })}
              style={{
                width: 30, height: 30, flexShrink: 0, borderRadius: 8,
                background: "transparent", border: `1px solid ${INK.line}`,
                color: INK.faint, fontFamily: FONT.forma, fontSize: 13,
                cursor: "pointer", touchAction: "manipulation",
              }}
            >×</button>
          </div>
        ))}
        {/* Add row */}
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 2 }}>
          <input
            value={newIngAmount}
            onChange={(e) => setNewIngAmount(e.target.value)}
            placeholder="2 tbsp"
            style={{ ...fieldStyle, width: 86, fontStyle: "italic", color: INK.warm, flexShrink: 0, borderStyle: "dashed" }}
          />
          <input
            value={newIngName}
            onChange={(e) => setNewIngName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addIngredient(); }}
            placeholder="add an ingredient…"
            style={{ ...fieldStyle, flex: 1, minWidth: 0, borderStyle: "dashed" }}
          />
          <button
            type="button"
            className="mcm-focusable"
            onClick={addIngredient}
            disabled={!newIngName.trim()}
            style={{
              width: 30, height: 30, flexShrink: 0, borderRadius: 8,
              background: newIngName.trim() ? INK.fill : "transparent",
              border: `1px solid ${INK.line}`,
              color: newIngName.trim() ? INK.deep : INK.faint,
              fontFamily: FONT.forma, fontSize: 15,
              cursor: newIngName.trim() ? "pointer" : "default",
              touchAction: "manipulation",
            }}
          >+</button>
        </div>
      </div>

      {/* Steps — text editing only in v1. */}
      {steps.length > 0 && (
        <>
          <div style={{ ...kickerStyle, marginTop: 20, marginBottom: 8 }}>
            STEPS · {steps.length}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {steps.map((step, i) => (
              <div key={step.id || i}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ ...kickerStyle, fontSize: 12, flexShrink: 0 }}>{i + 1}</span>
                  <input
                    value={step.title || ""}
                    onChange={(e) => patch((d) => { d.steps[i].title = e.target.value; })}
                    placeholder="Step title (optional)"
                    style={{ ...fieldStyle, flex: 1, fontWeight: 600 }}
                  />
                </div>
                <textarea
                  value={step.instruction || ""}
                  onChange={(e) => patch((d) => { d.steps[i].instruction = e.target.value; })}
                  rows={Math.min(6, Math.max(2, Math.ceil(String(step.instruction || "").length / 60)))}
                  placeholder="What to do…"
                  style={{
                    ...fieldStyle,
                    width: "100%", resize: "vertical",
                    lineHeight: 1.55,
                  }}
                />
              </div>
            ))}
          </div>
        </>
      )}

      {/* Fork name — only when this save will mint your own version. */}
      {saveMode === "fork" && dirty && (
        <div style={{ marginTop: 18 }}>
          <div style={{ ...kickerStyle, marginBottom: 6 }}>YOUR VERSION'S NAME</div>
          <input
            value={forkName}
            onChange={(e) => setForkName(e.target.value)}
            style={{ ...fieldStyle, width: "100%", fontFamily: FONT.serif, fontStyle: "italic", fontSize: 16 }}
          />
          <div style={{ marginTop: 4, fontFamily: FONT.forma, fontSize: 12, color: INK.faint }}>
            The original stays untouched — this saves a copy with your tweaks.
          </div>
        </div>
      )}

      {error && (
        <div style={{
          marginTop: 12, padding: "9px 11px",
          background: "rgba(163,59,42,0.10)", border: "1px solid rgba(163,59,42,0.35)",
          borderRadius: 8, fontFamily: FONT.forma, fontSize: 12, color: INK.rose,
        }}>
          {error}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, marginTop: 16, paddingBottom: 6 }}>
        <button
          type="button"
          className="mcm-focusable"
          onClick={() => onExit?.({ saved: false })}
          disabled={saving}
          style={{
            flex: 1, padding: "12px",
            background: "transparent", border: `1px solid ${INK.line}`,
            color: INK.deep, borderRadius: 8,
            fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
            letterSpacing: "0.12em", cursor: saving ? "default" : "pointer",
            touchAction: "manipulation",
          }}
        >
          CANCEL
        </button>
        <button
          type="button"
          className="mcm-focusable"
          onClick={handleDone}
          disabled={!dirty || saving}
          style={{
            flex: 2, padding: "12px",
            background: dirty && !saving
              ? "linear-gradient(180deg, #C85A1F 0%, #A34711 100%)"
              : "rgba(124,94,44,0.15)",
            border: "1px solid rgba(80,40,10,0.4)",
            color: dirty && !saving ? "#FFF8EE" : INK.faint,
            borderRadius: 8,
            fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
            letterSpacing: "0.12em",
            cursor: dirty && !saving ? "pointer" : "default",
            touchAction: "manipulation",
          }}
        >
          {saving ? "SAVING…" : saveMode === "fork" ? "SAVE MY VERSION" : "DONE"}
        </button>
      </div>
    </div>
  );
}
